import { createSignal, onCleanup, onMount } from 'solid-js';
import { networkConnection, type NetworkConnection } from '~/lib/platform';

export interface NetworkInfo {
  online: () => boolean;
  connection: () => NetworkConnection | null;
}

/** Reactive `navigator.onLine` + Network Information API view. */
export function createNetworkInfo(): NetworkInfo {
  const [online, setOnline] = createSignal(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [connection, setConnection] = createSignal<NetworkConnection | null>(networkConnection());

  onMount(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    const conn = networkConnection();
    let onChange: (() => void) | null = null;
    if (conn && 'addEventListener' in conn && typeof (conn as { addEventListener: unknown }).addEventListener === 'function') {
      onChange = () => setConnection({ ...networkConnection() });
      (conn as unknown as EventTarget).addEventListener('change', onChange);
    }
    onCleanup(() => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
      if (conn && onChange && 'removeEventListener' in conn) {
        (conn as unknown as EventTarget).removeEventListener('change', onChange);
      }
    });
  });

  return { online, connection };
}
